import React, { useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Input, Button } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import { assocPath, path, prop, isNotEmpty, propOr } from 'ramda';
import AccountLayout from '../../layout/account/AccountLayout';
import { emailIsValid, passwordIsValid } from '../../lib/validation';
import useKeyPressEnter from '../../hooks/useKeyPressEnter';
import useActionGuard from '../../hooks/useActionGuard';
import SignUpMutation from '../../_graphql/mutations/SignUpMutation';
import { errorNotification, successNotification } from '../../utils/messageMutation';
import EmailPasswordForm from './components/EmailPasswordForm';

const useSignUp = (email) => {
  const history = useHistory();
  // Email comes from a stable signup token in the current flow, so one-time initialization is intentional.
  const [values, setValues] = useState({
    email,
    password: '',
    extra: {
      firstname: '',
      lastname: ''
    }
  });
  const { loading, run } = useActionGuard();

  const setValue = (k, v) => setValues(assocPath(k.split('.'), v));

  const fieldIsDefined = k => isNotEmpty(path(k.split('.'), values));

  const onSignUp = () => {
    run(`sign-up-${email}`, (done) => {
      SignUpMutation(values, (ok, error) => {
        if (!ok || error) {
          errorNotification('L\'inscription a échoué');
        } else {
          successNotification('Inscription réussie !', 'Vous pouvez dès à présent vous connecter.');
          history.replace('/');
        }
        done(ok, error);
      });
    });
  };

  return {
    email: prop('email', values),
    emailIsValid: emailIsValid(prop('email', values)),
    passwordIsValid: passwordIsValid(prop('password', values)),
    fieldIsDefined,
    loading: propOr(false, `sign-up-${email}`, loading),
    setValue,
    onSignUp
  };
};

const SignUp = ({ email = '' }) => {
  const {
    email: currentEmail,
    emailIsValid,
    passwordIsValid,
    loading,
    fieldIsDefined,
    setValue,
    onSignUp
  } = useSignUp(email);

  const emailDisabled = isNotEmpty(email);
  const buttonDisabled = !fieldIsDefined('extra.firstname') || !fieldIsDefined('extra.lastname') || !passwordIsValid || !emailIsValid;
  useKeyPressEnter(onSignUp, !buttonDisabled, { emailIsValid, passwordIsValid });

  return (
    <AccountLayout>
      <h3>Créez votre espace flexidocs</h3>

      <div className="flex flex-col">
        <label>Prénom</label>
        <Input
          prefix={<UserOutlined/>}
          placeholder="Prénom"
          onChange={e => setValue('extra.firstname', e.target.value)}
        />
      </div>

      <div className="flex flex-col my-4">
        <label>Nom</label>
        <Input
          prefix={<UserOutlined/>}
          placeholder="Nom"
          onChange={e => setValue('extra.lastname', e.target.value)}
        />
      </div>

      <EmailPasswordForm
        emailValue={currentEmail}
        emailDisabled={emailDisabled}
        setValue={setValue} fieldIsDefined={fieldIsDefined} emailIsValid={emailIsValid}
        passwordIsValid={passwordIsValid}
      />

      <br/>

      <Button
        type="primary"
        size="large"
        disabled={buttonDisabled}
        loading={loading}
        onClick={onSignUp}
      >
        Suivant
      </Button>

      <div className="mt-4">
        Vous avez déjà un compte ? &nbsp;
        <Link to="/account/signin">Connectez-vous</Link>
      </div>
    </AccountLayout>
  );
};

export default SignUp;
